import React, { useEffect, useMemo } from 'react';
import { View, ActivityIndicator, Platform, StatusBar } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer, DarkTheme, DefaultTheme, createNavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { AuthProvider, useAuth } from './src/auth';
import LoginScreen from './src/screens/LoginScreen';
import HomeScreen from './src/screens/HomeScreen';
import ChatScreen from './src/screens/ChatScreen';
import RoomInfoScreen from './src/screens/RoomInfoScreen';
import WorkspaceScreen from './src/screens/WorkspaceScreen';
import NewChatScreen from './src/screens/NewChatScreen';
import SettingsScreen from './src/screens/SettingsScreen';
import ThreadScreen from './src/screens/ThreadScreen';
import { ThreadsScreen } from './src/screens/ThreadsScreen';
import type { RootStackParamList } from './src/navTypes';
import { useTheme } from './src/theme';
import { loadPrefs } from './src/prefs';
import { DesktopShell, WelcomeScreen, useIsDesktop } from './src/ui/DesktopShell';
import { applyWebChrome } from './src/ui/webChrome';
import { installWebAlert, WebAlertHost } from './src/ui/webAlert';
import { headerTitleFont, useAppFonts } from './src/fonts';

loadPrefs();
installWebAlert(); // web only: Alert.alert is an empty stub there, so confirms and error messages never showed

const Stack = createNativeStackNavigator<RootStackParamList>();
const navRef = createNavigationContainerRef<RootStackParamList>();

function Root() {
  const { phase } = useAuth();
  const theme = useTheme();
  const fonts = useAppFonts(); // true once the fonts are in, or once we gave up and stay on the system font
  const desktop = useIsDesktop(); // web + wide window only; native is always false
  useEffect(() => { applyWebChrome(theme); }, [theme]); // web only: scroll bars, focus rings, no sideways page scroll
  const navTheme = useMemo(() => {
    const base = theme.name === 'light' ? DefaultTheme : DarkTheme;
    return { ...base, colors: { ...base.colors, background: theme.bg, card: theme.surface, text: theme.ink, border: theme.border, primary: theme.accent } };
  }, [theme]);

  if (phase === 'loading' || !fonts) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.bg, alignItems: 'center', justifyContent: 'center' }}>
        <StatusBar barStyle={theme.statusBar} />
        <ActivityIndicator color={theme.accent} />
      </View>
    );
  }

  // On desktop web the sidebar is the Home screen, so the stack starts on an empty right pane.
  const navigator = (
      <Stack.Navigator
        key={desktop ? 'desktop' : 'phone'}
        screenOptions={{
          headerStyle: { backgroundColor: theme.surface },
          headerTintColor: theme.ink,
          headerTitleStyle: headerTitleFont(),
          contentStyle: { backgroundColor: theme.bg },
        }}
      >
        {phase === 'signedIn' ? (
          <>
            {desktop ? <Stack.Screen name="Welcome" component={WelcomeScreen} options={{ headerShown: false }} /> : null}
            <Stack.Screen name="Home" component={HomeScreen} options={{ headerShown: false }} />
            <Stack.Screen name="Chat" component={ChatScreen} options={{ title: 'Chat' }} />
            <Stack.Screen name="RoomInfo" component={RoomInfoScreen} options={{ title: 'Room' }} />
            <Stack.Screen name="Workspace" component={WorkspaceScreen} options={{ title: 'Workspace' }} />
            <Stack.Screen name="NewChat" component={NewChatScreen} options={{ title: 'New conversation' }} />
            <Stack.Screen name="Settings" component={SettingsScreen} options={{ title: 'Settings' }} />
            <Stack.Screen name="Thread" component={ThreadScreen} options={{ title: 'Thread' }} />
            <Stack.Screen name="Threads" component={ThreadsScreen} options={{ title: 'Threads' }} />
          </>
        ) : (
          <Stack.Screen name="Login" component={LoginScreen} options={{ headerShown: false }} />
        )}
      </Stack.Navigator>
  );

  return (
    <NavigationContainer theme={navTheme} ref={navRef}>
      <StatusBar barStyle={theme.statusBar} />
      {phase === 'signedIn' && desktop ? <DesktopShell navRef={navRef}>{navigator}</DesktopShell> : navigator}
    </NavigationContainer>
  );
}

export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <AuthProvider>
          <Root />
          {Platform.OS === 'web' ? <WebAlertHost /> : null}
        </AuthProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}
